import { CircleAlert, CircleCheck, type LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/utils/classnames';
import type { BulletListBlock as BulletListBlockType } from '@/shared/types/blocks';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip';

/**
 * 아이콘 이름 → Lucide 컴포넌트 매핑
 * Interview Coach 강점/보완 필요 섹션에서 사용
 */
const iconMap: Record<string, { component: LucideIcon; className: string }> = {
  'circle-check': { component: CircleCheck, className: 'text-accent-green' },
  'circle-alert': { component: CircleAlert, className: 'text-accent-red' },
};

/**
 * BulletListBlock - 불릿 리스트
 *
 * Figma 디자인 기준:
 * - default: paragraph-large (18px, 1.5lh), label-alternative - 핵심 요점용
 * - compact: paragraph-medium (16px, 1.5lh), label-alternative - 챕터 카드 내부용
 * - 불릿: 4x4px, label-assistive
 * - 아이템 간격: gap-1 (4px)
 */
export function BulletListBlock({
  items,
  variant = 'default',
}: Omit<BulletListBlockType, 'type' | 'id'>) {
  const textStyle =
    variant === 'compact'
      ? 'paragraph-medium text-label-alternative' // 16px - 챕터 카드 내부
      : 'paragraph-large text-label-alternative'; // 18px - 핵심 요점
  return (
    <TooltipProvider>
      <div className="flex flex-col items-start gap-1 self-stretch">
        {items.map((item, index) => {
          const iconConfig = item.icon ? iconMap[item.icon] : null;
          const IconComponent = iconConfig?.component;

          return (
            <div key={`${item.text}-${index}`} className="flex items-start gap-1">
              {IconComponent ? (
                <IconComponent className={cn('size-5 shrink-0 mt-[5px]', iconConfig.className)} />
              ) : (
                <span className="w-1 h-1 rounded-[1px] bg-label-assistive mt-[11px] shrink-0" />
              )}
              <div className="flex items-center gap-2">
                <span className={textStyle}>{item.text}</span>
                {item.tooltip && Array.isArray(item.tooltip) && item.tooltip.length > 0 && (
                  <Tooltip>
                    <TooltipTrigger>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="shrink-0 cursor-pointer text-label-intermediate"
                      >
                        <title>Info</title>
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M1.66675 10.0003C1.66675 5.39795 5.39771 1.66699 10.0001 1.66699C14.6025 1.66699 18.3334 5.39795 18.3334 10.0003C18.3334 14.6027 14.6025 18.3337 10.0001 18.3337C5.39771 18.3337 1.66675 14.6027 1.66675 10.0003ZM10.8334 6.25033C10.8334 6.71056 10.4603 7.08366 10.0001 7.08366C9.53985 7.08366 9.16675 6.71056 9.16675 6.25033C9.16675 5.79009 9.53985 5.41699 10.0001 5.41699C10.4603 5.41699 10.8334 5.79009 10.8334 6.25033ZM10.8334 14.5837V8.33366H9.16675V14.5837H10.8334Z"
                          fill="currentColor"
                        />
                      </svg>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      align="start"
                      className="rounded-lg p-2 flex flex-col items-start border-none"
                    >
                      <ul className="flex flex-col">
                        {item.tooltip.map((tooltipItem: string) => (
                          <li key={tooltipItem} className="flex items-start gap-1.5 pl-2">
                            <span className="w-1 h-1 rounded-full bg-label-weakest mt-[7px] shrink-0" />
                            <span className="label-smaller text-white">{tooltipItem}</span>
                          </li>
                        ))}
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
